import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import ChatFeed from "@/components/ChatFeed";
import TaskBoard from "@/components/TaskBoard";
import MyTasks from "@/components/MyTasks";
import AIAssistant from "@/components/AIAssistant";
import { AIChat } from "@/components/AIChat";
import RecordingStudio from "@/components/RecordingStudio";
import ThreadPanel from "@/components/ThreadPanel";
import Meetings from "@/components/Meetings";
import AdminDashboard from "@/components/AdminDashboard";
import VideoRoom from "@/components/VideoRoom";
import UserProfile from "@/components/UserProfile";
import DirectMessages from "@/components/DirectMessages";
import IncomingCall from "@/components/IncomingCall";
import { EmergentBanner } from "@/components/EmergentPresence";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ListChecks, X } from "lucide-react";

const STUDIO = {
  id: "recordings",
  name: "recording-studio",
  description: "Record a meeting — get the raw voice note, AI notes and a to-do list.",
};

export default function Workspace() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState("general");
  const [messages, setMessages] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [mobileSidebar, setMobileSidebar] = useState(false);
  const [mobileTasks, setMobileTasks] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [emergentActive, setEmergentActive] = useState([]);
  const [meEmergent, setMeEmergent] = useState(false);
  const [profileUserId, setProfileUserId] = useState(null);
  const [dmUserId, setDmUserId] = useState(null);
  const [dms, setDms] = useState([]);
  const [activeCall, setActiveCall] = useState(null); // { channel, title }
  const meEmergentRef = useRef(false);
  meEmergentRef.current = meEmergent;
  const activeRef = useRef(active);
  activeRef.current = active;
  const pendingDeleteRef = useRef(new Map());

  const isStudio = active === "recordings";
  const isMeetings = active === "meetings";
  const isDM = active === "dm";
  const isAdmin = active === "admin";
  const META = {
    recordings: STUDIO,
    meetings: { id: "meetings", name: "meetings", description: "Start or join a team video call." },
    admin: { id: "admin", name: "admin dashboard", description: "Manage members and monitor the workspace." },
  };
  const activeChannel = META[active] || channels.find((c) => c.id === active);

  useEffect(() => {
    api.get("/channels").then((r) => setChannels(r.data)).catch((err) => console.error("Failed to load channels:", err));
  }, []);

  const goToNotification = useCallback((target) => {
    const query = (typeof target === "string" ? target : target?.url || "").split("?")[1] || "";
    const params = new URLSearchParams(query);
    if (params.get("tab") === "recordings") {
      setActive("recordings");
      return;
    }
    if (params.get("tasks") === "1") {
      setMobileTasks(true);
      return;
    }
    const channel = params.get("channel");
    if (channel) setActive(channel);
    const thread = params.get("thread");
    if (thread) setThreadId(thread);
    const dm = params.get("dm");
    if (dm) {
      setDmUserId(dm);
      setActive("dm");
    }
  }, []);

  // deep link from a clicked push notification
  useEffect(() => {
    if (window.location.search) {
      goToNotification(window.location.href);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [goToNotification]);

  const loadMessages = useCallback(async (ch) => {
    if (["recordings", "questions", "todo-list", "meetings", "dm", "admin"].includes(ch)) return;
    try {
      const r = await api.get(`/channels/${ch}/messages`);
      if (activeRef.current === ch) setMessages(r.data);
    } catch (err) {
      console.error("Failed to load messages:", err);
    }
  }, []);

  const loadTasks = useCallback(async () => {
    try {
      const r = await api.get("/tasks");
      const pending = pendingDeleteRef.current;
      setTasks(pending.size ? r.data.filter((t) => !pending.has(t.id)) : r.data);
    } catch (err) {
      console.error("Failed to load tasks:", err);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const r = await api.get("/users");
      setUsers(r.data);
    } catch (err) {
      console.error("Failed to load members:", err);
    }
  }, []);

  const loadDms = useCallback(async () => {
    try {
      const r = await api.get("/dms");
      setDms(r.data);
    } catch (err) {
      /* ignore */
    }
  }, []);

  const loadEmergent = useCallback(async () => {
    try {
      const r = await api.get("/emergent/active");
      setEmergentActive(r.data.active);
      setMeEmergent(r.data.me_active);
      if (r.data.me_active) api.post("/emergent/heartbeat").catch(() => {});
    } catch (err) {
      console.error("Failed to load Emergent presence:", err);
    }
  }, []);

  const toggleEmergent = async () => {
    const next = !meEmergentRef.current;
    setMeEmergent(next);
    try {
      await api.post(next ? "/emergent/start" : "/emergent/stop");
      loadEmergent();
    } catch {
      toast.error("Couldn't update your Emergent status");
      loadEmergent();
    }
  };

  // switch channel
  useEffect(() => {
    setMessages([]);
    loadMessages(active);
  }, [active, loadMessages]);

  // polling for live sync
  useEffect(() => {
    const msgInt = setInterval(() => loadMessages(activeRef.current), 2500);
    const taskInt = setInterval(loadTasks, 2500);
    const userInt = setInterval(loadUsers, 8000);
    const emergentInt = setInterval(loadEmergent, 3000);
    const dmInt = setInterval(loadDms, 5000);
    loadTasks();
    loadUsers();
    loadEmergent();
    loadDms();
    return () => {
      clearInterval(msgInt);
      clearInterval(taskInt);
      clearInterval(userInt);
      clearInterval(emergentInt);
      clearInterval(dmInt);
    };
  }, [loadMessages, loadTasks, loadUsers, loadEmergent, loadDms]);

  const sendMessage = async (content, attachments) => {
    try {
      const r = await api.post(`/channels/${active}/messages`, { content, attachments });
      setMessages((m) => [...m, r.data]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send");
    }
  };

  const startDM = (u) => {
    setDmUserId(u.id);
    setActive("dm");
    setMobileSidebar(false);
  };

  const startCall = async (u) => {
    try {
      await api.post("/calls/ring", { to_id: u.id });
      const chan = `call-${[user?.id, u.id].sort().join("__")}`;
      setActiveCall({ channel: chan, title: `Call with ${u.name}` });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start the call");
    }
  };

  const reactToMessage = async (id, emoji) => {
    setMessages((ms) =>
      ms.map((m) => {
        if (m.id !== id) return m;
        const reactions = { ...(m.reactions || {}) };
        const list = [...(reactions[emoji] || [])];
        const idx = list.findIndex((r) => r.user_id === user?.id);
        if (idx >= 0) list.splice(idx, 1);
        else list.push({ user_id: user?.id, name: user?.name });
        if (list.length) reactions[emoji] = list;
        else delete reactions[emoji];
        return { ...m, reactions };
      })
    );
    try {
      const r = await api.post(`/messages/${id}/reactions`, { emoji });
      setMessages((ms) => ms.map((m) => (m.id === id ? r.data : m)));
    } catch {
      toast.error("Could not add reaction");
      loadMessages(activeRef.current);
    }
  };

  const editMessage = async (id, content) => {
    try {
      const r = await api.patch(`/messages/${id}`, { content });
      setMessages((ms) => ms.map((m) => (m.id === id ? r.data : m)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't edit message");
    }
  };

  const deleteMessage = (id) => {
    setMessages((ms) => ms.filter((m) => m.id !== id));
    api.delete(`/messages/${id}`).catch(() => {
      toast.error("Couldn't delete message");
      loadMessages(activeRef.current);
    });
  };

  const createTask = async (title) => {    try {
      const r = await api.post("/tasks", { title, priority: "medium" });
      setTasks((t) => [r.data, ...t]);
    } catch {
      toast.error("Failed to create task");
    }
  };

  const updateTask = async (id, body) => {
    try {
      const r = await api.patch(`/tasks/${id}`, body);
      setTasks((t) => t.map((x) => (x.id === id ? r.data : x)));
    } catch {
      toast.error("Failed to update task");
    }
  };

  const deleteTask = (id) => {
    const task = tasks.find((x) => x.id === id);
    if (!task) return;
    // optimistic remove + keep filtered from polls until confirmed
    setTasks((t) => t.filter((x) => x.id !== id));
    pendingDeleteRef.current.set(id, task);
    // backend soft-deletes immediately, so it never comes back on the next poll
    api.delete(`/tasks/${id}`).catch(() => loadTasks());
    toast("Task deleted", {
      description: task.title.length > 64 ? `${task.title.slice(0, 64)}…` : task.title,
      duration: 6000,
      onAutoClose: () => pendingDeleteRef.current.delete(id),
      onDismiss: () => pendingDeleteRef.current.delete(id),
      action: {
        label: "Undo",
        onClick: async () => {
          try {
            const r = await api.post(`/tasks/${id}/restore`);
            pendingDeleteRef.current.delete(id);
            setTasks((t) => (t.some((x) => x.id === id) ? t : [r.data, ...t]));
          } catch {
            pendingDeleteRef.current.delete(id);
            loadTasks();
          }
        },
      },
    });
  };

  const aiAddTasks = async (message) => {
    try {
      const r = await api.post("/ai/message", { message, source: "chat" });
      const count = r.data.created_tasks?.length || 0;
      if (count) {
        toast.success(`${count} task(s) added by AI`);
        loadTasks();
      } else {
        toast.message("AI didn't add a task — try describing something to do");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI couldn't add that");
    }
  };

  const onlineCount = users.filter((u) => u.online).length;
  const myEmergentStart = emergentActive.find((a) => a.user_id === user?.id)?.started_at;

  const selectChannel = (id) => {
    setActive(id);
    setMobileSidebar(false);
  };

  return (
    <div className="flex overflow-hidden bg-[#0B0C10]" style={{ height: "var(--app-height, 100dvh)" }}>
      {/* Desktop sidebar */}
      <div className="hidden md:block shrink-0">
        <Sidebar
          channels={channels}
          active={active}
          onSelect={selectChannel}
          users={users}
          dms={dms}
          isAdmin={user?.role === "admin"}
          onSelectUser={(u) => setProfileUserId(u.id)}
          onOpenDM={(id) => { setDmUserId(id); setActive("dm"); }}
          onOpenVoice={() => setVoiceOpen(true)}
        />
      </div>

      {/* Mobile sidebar */}
      <Sheet open={mobileSidebar} onOpenChange={setMobileSidebar}>
        <SheetContent side="left" className="p-0 w-[260px] border-[#2E3242] bg-[#121318]">
          <SheetTitle className="sr-only">Channels and members</SheetTitle>
          <SheetDescription className="sr-only">
            Switch channels, open the recording studio, or see who is online.
          </SheetDescription>
          <Sidebar
            channels={channels}
            active={active}
            onSelect={selectChannel}
            users={users}
            dms={dms}
            isAdmin={user?.role === "admin"}
            onSelectUser={(u) => { setMobileSidebar(false); setProfileUserId(u.id); }}
            onOpenDM={(id) => { setMobileSidebar(false); setDmUserId(id); setActive("dm"); }}
            variant="mobile"
            onOpenVoice={() => {
              setMobileSidebar(false);
              setVoiceOpen(true);
            }}
          />
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col min-w-0">
        <Header
          channel={activeChannel}
          channels={channels}
          onlineCount={onlineCount}
          onOpenVoice={() => setVoiceOpen(true)}
          onOpenSidebar={() => setMobileSidebar(true)}
          onNotificationNavigate={goToNotification}
          emergentActive={emergentActive}
          meEmergent={meEmergent}
          myEmergentStart={myEmergentStart}
          onToggleEmergent={toggleEmergent}
        />
        <EmergentBanner active={emergentActive} onStop={toggleEmergent} />
        <div className="flex-1 flex min-h-0">
          {isStudio ? (
            <RecordingStudio onTasksChanged={loadTasks} />
          ) : isAdmin ? (
            <AdminDashboard />
          ) : isMeetings ? (
            <Meetings />
          ) : isDM ? (
            <DirectMessages
              otherId={dmUserId}
              onBack={() => setActive("general")}
              onCall={startCall}
            />
          ) : active === "questions" ? (
            <AIChat
              active
              onTasksChanged={loadTasks}
              title="Ask the AI"
              testId="questions-ai-chat"
              className="flex-1 bg-[#16181F]"
            />
          ) : active === "todo-list" ? (
            <MyTasks tasks={tasks} onUpdate={updateTask} onDelete={deleteTask} />
          ) : (
            <ChatFeed
              channel={activeChannel}
              messages={messages}
              onSend={sendMessage}
              onOpenVoice={() => setVoiceOpen(true)}
              onReact={reactToMessage}
              onOpenThread={setThreadId}
              onEditMessage={editMessage}
              onDeleteMessage={deleteMessage}
            />
          )}
          {/* Desktop task board — hidden in full-bleed views */}
          {!isMeetings && !isDM && !isAdmin && (
            <div className="hidden lg:block shrink-0">
              <TaskBoard
                tasks={tasks}
                users={users}
                onCreate={createTask}
                onUpdate={updateTask}
                onDelete={deleteTask}
                onAiAdd={aiAddTasks}
              />
            </div>
          )}
        </div>
      </div>

      {/* Mobile task board trigger */}
      <button
        data-testid="mobile-tasks-button"
        onClick={() => setMobileTasks(true)}
        className="lg:hidden fixed bottom-20 right-4 z-30 w-14 h-14 rounded-full ruby-gradient border border-red-900/40 ember-glow flex items-center justify-center"
      >
        <ListChecks className="w-6 h-6 text-white" />
        {tasks.filter((t) => t.status !== "done").length > 0 && (
          <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center border border-[#0B0C10]">
            {tasks.filter((t) => t.status !== "done").length}
          </span>
        )}
      </button>

      <Sheet open={mobileTasks} onOpenChange={setMobileTasks}>
        <SheetContent side="right" className="p-0 w-full sm:w-[360px] border-[#2E3242] bg-[#121318]">
          <SheetTitle className="sr-only">Task board</SheetTitle>
          <SheetDescription className="sr-only">
            Create, claim and complete team tasks.
          </SheetDescription>
          <TaskBoard
            tasks={tasks}
            users={users}
            onCreate={createTask}
            onUpdate={updateTask}
            onDelete={deleteTask}
            onAiAdd={aiAddTasks}
            variant="mobile"
          />
        </SheetContent>
      </Sheet>

      <AIAssistant open={voiceOpen} onOpenChange={setVoiceOpen} onTasksChanged={loadTasks} />

      <ThreadPanel
        messageId={threadId}
        onOpenChange={setThreadId}
        onReplied={() => loadMessages(activeRef.current)}
      />

      {/* Member profile */}
      <UserProfile
        userId={profileUserId}
        open={!!profileUserId}
        onOpenChange={(o) => !o && setProfileUserId(null)}
        onMessage={startDM}
        onCall={startCall}
        selfId={user?.id}
      />

      {/* Incoming 1:1 call ringer (paused while already in a call) */}
      <IncomingCall
        disabled={!!activeCall}
        onAccept={({ channel, title }) => setActiveCall({ channel, title })}
      />

      {/* Active 1:1 call overlay */}
      <Dialog open={!!activeCall} onOpenChange={(o) => !o && setActiveCall(null)}>
        <DialogContent className="w-screen h-[100dvh] max-w-none rounded-none sm:w-[95vw] sm:h-[85vh] sm:max-w-5xl sm:rounded-lg p-0 bg-[#0F1015] border-[#2E3242] overflow-hidden">
          <DialogTitle className="sr-only">{activeCall?.title || "Video call"}</DialogTitle>
          <DialogDescription className="sr-only">Live video call.</DialogDescription>
          {activeCall && (
            <VideoRoom
              channel={activeCall.channel}
              title={activeCall.title}
              onLeave={() => setActiveCall(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
